// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type { ManufacturerId, UnitOfMeasureId, UnitOfMeasure, Manufacturer } from '@evolvemep/api-ts';
import { Flex, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import { NumberInputWrapped } from 'components/Mantine/NumberInputWrapped';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import type { StrongOmit, WithNullableExcept } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { ManufacturerSelect } from 'hooks/typeCacheHooks/ManufacturerSelect';
import { UnitOfMeasureSelect } from 'hooks/typeCacheHooks/UnitOfMeasureSelect';

export type WriteInItemFields = {
  name: string;
  manufacturerCatalogNumber: string;
  description: string | null;
  quantity: number;
  manufacturerId: ManufacturerId;
  unitOfMeasureId: UnitOfMeasureId;
  unitOfMeasureName: string;
  unitOfMeasureCode: string;
};

type FormValues = StrongOmit<
  WriteInItemFields,
  'unitOfMeasureId' | 'unitOfMeasureName' | 'unitOfMeasureCode' | 'manufacturerId'
> & {
  unitOfMeasure: UnitOfMeasure;
  manufacturer: Manufacturer;
};

export type EditItemPopoverProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: WriteInItemFields) => Promise<void>;
  children?: ReactNode;
  loading?: boolean;
  defaultValues?: Partial<FormValues>;
};

const EditItemFields = ({
  onSubmit,
  loading,
  defaultValues,
}: Pick<EditItemPopoverProps, 'onSubmit' | 'loading' | 'defaultValues'>) => {
  const form = useForm<WithNullableExcept<FormValues, 'name' | 'description'>>({
    initialValues: {
      name: '',
      description: '',
      manufacturerCatalogNumber: '',
      quantity: null,
      unitOfMeasure: null,
      manufacturer: null,
      ...defaultValues,
    },
    validate: {
      name: (n) => !n,
      quantity: (q) => isNil(q) || q <= 0 || q > 999999,
      unitOfMeasure: (v) => !v,
      manufacturer: (v) => !v,
    },
    enhanceGetInputProps: () => ({ disabled: loading }),
  });

  const wrappedOnSubmit = async (rawValues: typeof form.values) => {
    const { unitOfMeasure, manufacturer, ...values } = rawValues as FormValues;
    await onSubmit({
      // Cast is safe due to form.validate
      ...(values as Omit<FormValues, 'unitOfMeasure'>),
      description: values.description || null,
      manufacturerCatalogNumber: values.manufacturerCatalogNumber,
      manufacturerId: manufacturer.manufacturerId,
      unitOfMeasureId: unitOfMeasure.unitOfMeasureId,
      // Only used to allow the UI to display these fields;
      // they are not read by the backend
      unitOfMeasureName: unitOfMeasure.name,
      unitOfMeasureCode: unitOfMeasure.code,
    });
  };

  useFormPopover(form, wrappedOnSubmit);

  return (
    <Flex direction="column" gap="sm">
      <TextInput label="Item Name" required autoFocus data-autofocus {...form.getInputProps('name')} />
      <Flex gap="sm">
        <NumberInputWrapped
          miw={90}
          max={999_999}
          clampBehavior="strict"
          min={1}
          label="Qty"
          required
          allowDecimal={false}
          {...form.getInputProps('quantity')}
        />
        <Flex w="100%">
          <UnitOfMeasureSelect required flex={1} {...form.getInputProps('unitOfMeasure')} />
        </Flex>
      </Flex>
      <ManufacturerSelect required defaultToGeneric {...form.getInputProps('manufacturer')} />
      <TextInput label="Catalog #" maxLength={64} {...form.getInputProps('manufacturerCatalogNumber')} />
    </Flex>
  );
};

export const EditItemPopover = ({
  opened,
  onClose,
  loading,
  onSubmit,
  children,
  defaultValues,
}: EditItemPopoverProps) => (
  <FormPopover
    width={350}
    opened={opened}
    onClose={onClose}
    loading={loading}
    target={children}
    confirmationText={defaultValues ? 'Save' : 'Add'}
  >
    <EditItemFields onSubmit={onSubmit} loading={loading} defaultValues={defaultValues} />
  </FormPopover>
);
